import { Form, Input, DatePicker, message, Select } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { handleError, sanitizeFormValue, validateForm } from 'utils/form';
import FormContainer from 'components/ui/form-container';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormActionButtons from 'components/ui/form-action-buttons';
import { ENavigationPath } from 'constants/path';
import { fields, getValidationSchema, initialValues } from './form';
import { CreateCatEventPayloadDto } from 'libs/pinnacle-sdk';
import { useMutation, useQuery } from 'react-query';
import { catEventApi, countryStateCityApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import CalendarIcon from 'assets/icons/calendar';
import { debounce } from 'lodash';
import { SortedSelect } from 'components/ui/sorted-select';
import TextArea from 'antd/es/input/TextArea';

interface CatEventFormProps {
  mode?: 'create' | 'update';
  initialData?: object;
  onSubmit?: (data: CreateCatEventPayloadDto) => Promise<void>;
  isLoading?: boolean;
}

const CatEventForm = ({
  mode = 'create',
  initialData = initialValues,
  onSubmit,
  isLoading = false,
}: CatEventFormProps) => {
  const [form] = Form.useForm<CreateCatEventPayloadDto>();
  const [isFormValid, setIsFormValid] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => {
      return countryStateCityApi.getCountries();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const createCatEventMutation = useMutation(
    (catEvent: CreateCatEventPayloadDto) =>
      catEventApi.createCatEvent({
        createCatEventPayloadDto: catEvent,
      }),
    {
      onSuccess: () => {
        message.success(
          t('forms.createRecordSuccess', { type: t('catEvent.catEvent') })
        );
        resetAndNavigate();
      },
      onError: async (error: { response: Response }) =>
        handleError({
          error,
          defaultMessage: t('forms.createRecordFail', {
            type: t('catEvent.catEvent'),
          }),
        }),
    }
  );

  const debouncedUpsertCatEventMutate = useCallback(
    debounce((catEvent: CreateCatEventPayloadDto) => {
      if (mode === 'update' && onSubmit) {
        onSubmit(catEvent)
          .then(() => {
            message.success(
              t('forms.updateRecordSuccess', { type: t('catEvent.catEvent') })
            );
            navigate(ENavigationPath.CAT_EVENT_RECORDS);
          })
          .catch((error) => {
            handleError({
              error,
              defaultMessage: t('forms.updateRecordFail', {
                type: t('catEvent.catEvent'),
              }),
            });
          });
      } else {
        createCatEventMutation.mutate(catEvent);
      }
    }, 500),
    [createCatEventMutation, mode, onSubmit, navigate, t]
  );

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
      // Validate form after setting initial values
      validateForm<CreateCatEventPayloadDto>(
        initialData as CreateCatEventPayloadDto,
        form,
        fields,
        getValidationSchema(t)
      ).then(setIsFormValid);
    }
  }, [form, initialData, t]);

  const resetAndNavigate = useCallback(() => {
    form.resetFields();
    return navigate(ENavigationPath.CAT_EVENT_RECORDS);
  }, [form, navigate]);

  return (
    <FormContainer
      title={
        mode === 'create'
          ? t('catEvent.setupNewCatEvent')
          : t('catEvent.updateCatEvent')
      }
      loading={isLoading}
    >
      <Form
        form={form}
        layout="vertical"
        className="space-y-8"
        initialValues={initialValues}
        onValuesChange={async () => {
          const values = form.getFieldsValue();
          const isValid = await validateForm<CreateCatEventPayloadDto>(
            values,
            form,
            fields,
            getValidationSchema(t)
          );
          setIsFormValid(isValid);
        }}
        onFinish={async (catEvent: CreateCatEventPayloadDto) => {
          const sanitizedCatEvent = sanitizeFormValue(catEvent);
          debouncedUpsertCatEventMutate(sanitizedCatEvent);
        }}
      >
        <FormSection title={t('catEvent.catEventDetails')}>
          <Form.Item name="eventName" label={t('catEvent.eventName')}>
            <Input
              size="large"
              placeholder={t('general.enter', {
                field: t('catEvent.eventName'),
              })}
            />
          </Form.Item>
          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="fromDate" label={t('catEvent.fromDate')}>
              <DatePicker
                allowClear
                format={'DD/MM/YYYY'}
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                onChange={(date) => {
                  const endDate = form.getFieldValue('toDate');
                  if (endDate && date && endDate < date) {
                    form.setFieldValue('toDate', null);
                  }
                }}
              />
            </Form.Item>

            <Form.Item name="toDate" label={t('catEvent.toDate')}>
              <DatePicker
                allowClear
                format={'DD/MM/YYYY'}
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                disabledDate={(current) => {
                  const startDate = form.getFieldValue('fromDate');
                  return startDate ? current < startDate : false;
                }}
              />
            </Form.Item>
          </FormLayoutGroup>
          <Form.Item name="country" label={t('general.country')}>
            <SortedSelect
              allowClear
              size="large"
              placeholder={t('general.selectField', {
                field: t('general.country'),
              })}
              showSearch
              loading={isLoadingCountries}
              options={(countries || []).map((country) => ({
                label: country.name,
                value: country.name,
              }))}
              suffixIcon={<ArrowDownIcon />}
            ></SortedSelect>
          </Form.Item>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item name="description" label={t('general.details')}>
              <TextArea
                rows={4}
                placeholder={t('general.enter', {
                  field: t('general.comments'),
                })}
              ></TextArea>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>
        <FormActionButtons
          onCancel={resetAndNavigate}
          isConfirmDisabled={
            !isFormValid || isLoading || createCatEventMutation.isLoading
          }
          confirmText={t(
            mode === 'create' ? 'catEvent.createCatEvent' : 'general.save'
          )}
        />
      </Form>
    </FormContainer>
  );
};

export default CatEventForm;
